import React, { ReactElement } from 'react';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import { t, defineMessages } from '@sm/intl';
import { Immutable } from '@wds/utils';
import { Box } from '@wds/box';
import { IconBell } from '@wds/icons';
import { List } from '@wds/list';
import { Responsive } from '@wds/responsive';
import { NavButton } from '../nav-button';
import useStyles from './useStyles';

export const COPY = defineMessages({
  NOTIFICATION_CENTER: {
    id: 'NotificationsIcon.notificationCenter',
    defaultMessage: 'Notification center',
    description:
      '[Type: Label][Vis.: high] - This is the main text for the title of the this notifications feature.',
  },
  ICON_ALT_TEXT: {
    id: 'NotificationsIcon.altText',
    defaultMessage: 'Notification center icon',
    description:
      '[Type: Label][Vis.: high] - This is the alt text that displays to screens readers focused on the bell icon.',
  },
  MAX_COUNT: {
    id: 'NotificationsIcon.maxCount',
    defaultMessage: '9+',
    description:
      '[Type: Label][Vis.: high] - The badge displaying message count should be limited to a maximum of "9+".',
  },
  NAV_BUTTON_LABEL: {
    id: 'NotificationsIcon.navLabel',
    defaultMessage: 'See my notifications',
    description:
      '[Type: Label][Vis.: high] - Alt text for the navigation item since the navbutton since it contains only an icon.',
  },
});

export type NotificationsIconProps = Immutable<{
  showNotifications: boolean;
  unreadNotificationsCount: number;
  pageName?: string;
  pageURL?: string;
}> & {
  toggleNotifications(): void;
  refetchNotificationIconCount(): void;
};

const NotificationsIcon = ({
  showNotifications,
  toggleNotifications,
  unreadNotificationsCount,
  pageName,
  pageURL,
  refetchNotificationIconCount,
}: NotificationsIconProps): ReactElement => {
  const {
    notificationsBadge,
    notificationsBadgeContainer,
    notificationsBadgeContainerMobile,
  } = useStyles();

  const trackNotificationsEvent = () => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        amplitudeEvent: 'notification center interaction',
        interactionType: 'click',
        interactionLocation: 'bell icon',
        messageCountUnread: unreadNotificationsCount,
        pageName,
        pageURL,
      },
    });
  };

  const maxUnreadNotificationsCount =
    unreadNotificationsCount > 9 ? t(COPY.MAX_COUNT) : unreadNotificationsCount;

  return (
    <Responsive minWidth="xl">
      {(matches: boolean) =>
        matches ? (
          <NavButton
            onClick={() => {
              toggleNotifications();
              trackNotificationsEvent();
              refetchNotificationIconCount();
            }}
            label={t(COPY.NAV_BUTTON_LABEL)}
            active={showNotifications}
          >
            <Box>
              {unreadNotificationsCount > 0 && (
                <div className={notificationsBadgeContainer}>
                  <div className={notificationsBadge}>
                    {maxUnreadNotificationsCount}
                  </div>
                </div>
              )}
              <IconBell title={t(COPY.ICON_ALT_TEXT)} size="lg" />
            </Box>
          </NavButton>
        ) : (
          <List.Item
            onClick={toggleNotifications}
            affordance={
              unreadNotificationsCount > 0 ? (
                <div className={notificationsBadgeContainerMobile}>
                  <div className={notificationsBadge}>
                    {maxUnreadNotificationsCount}
                  </div>
                </div>
              ) : undefined
            }
            role="button"
          >
            {t(COPY.NOTIFICATION_CENTER)}
          </List.Item>
        )
      }
    </Responsive>
  );
};

export default NotificationsIcon;
export { NotificationsIcon };
